import { api } from './client';
import type { Vehicle, HandlingLogEntry, AuditLogEntry, FilterState } from '../types';

function buildQuery(filters: Partial<FilterState>): string {
  const params = new URLSearchParams();
  if (filters.search)             params.set('search', filters.search);
  if (filters.vehicle_class?.length)  params.set('vehicle_class', filters.vehicle_class.join(','));
  if (filters.category?.length)   params.set('category', filters.category.join(','));
  if (filters.tier?.length)       params.set('tier', filters.tier.join(','));
  if (filters.progress?.length)   params.set('progress', filters.progress.join(','));
  if (filters.dealership_id)      params.set('dealership_id', filters.dealership_id);
  if (filters.dealership_available) params.set('dealership_available', 'true');
  if (filters.boosting_exclusive) params.set('boosting_exclusive', 'true');
  if (filters.rp_exclusive)       params.set('rp_exclusive', 'true');
  if (filters.min_doors)          params.set('min_doors', filters.min_doors);
  if (filters.max_doors)          params.set('max_doors', filters.max_doors);
  if (filters.min_seats)          params.set('min_seats', filters.min_seats);
  if (filters.max_seats)          params.set('max_seats', filters.max_seats);
  if (filters.min_speed)          params.set('min_speed', filters.min_speed);
  if (filters.max_speed)          params.set('max_speed', filters.max_speed);
  if (filters.tag_ids?.length)    params.set('tag_ids', filters.tag_ids.join(','));
  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

export const vehiclesApi = {
  list: (filters: Partial<FilterState> = {}) =>
    api.get<{ vehicles: Vehicle[]; total: number }>(`/vehicles${buildQuery(filters)}`),

  get: (id: number) => api.get<{ vehicle: Vehicle }>(`/vehicles/${id}`),

  create: (data: Partial<Vehicle> & { tag_ids?: number[] }) =>
    api.post<{ vehicle: Vehicle }>('/vehicles', data),

  update: (id: number, data: Partial<Vehicle> & { tag_ids?: number[]; audit_note?: string }) =>
    api.put<{ vehicle: Vehicle }>(`/vehicles/${id}`, data),

  patch: (id: number, data: Partial<Vehicle> & { tag_ids?: number[]; audit_note?: string }) =>
    api.patch<{ vehicle: Vehicle }>(`/vehicles/${id}`, data),

  delete: (id: number) => api.delete<{ success: boolean }>(`/vehicles/${id}`),

  uploadImage: (id: number, file: File) => {
    const fd = new FormData();
    fd.append('image', file);
    return api.upload<{ image_key: string; image_url: string }>(`/vehicles/${id}/image`, fd);
  },

  setImageUrl: (id: number, url: string) =>
    api.post<{ image_url: string }>(`/vehicles/${id}/image/url`, { url }),

  deleteImage: (id: number) => api.delete<{ success: boolean }>(`/vehicles/${id}/image`),

  getHandlingLog: (id: number) =>
    api.get<{ log: HandlingLogEntry[]; total_minutes: number }>(`/vehicles/${id}/log`),

  addHandlingLog: (id: number, data: { notes?: string; time_spent_minutes: number }) =>
    api.post<{ entry: HandlingLogEntry }>(`/vehicles/${id}/log`, data),

  deleteHandlingLog: (vehicleId: number, logId: number) =>
    api.delete<{ success: boolean }>(`/vehicles/${vehicleId}/log/${logId}`),

  getAuditLog: (id: number) =>
    api.get<{ log: AuditLogEntry[] }>(`/vehicles/${id}/audit`),

  exportCsv: () => fetch('/api/export/csv', { credentials: 'include' }),
};
